import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { cdnImg } from "../lib/img";
import { SignOut, Star, List, X, CaretDown, Sparkle, User, Receipt, SquaresFour, Handshake } from "@phosphor-icons/react";
import { POLICY_ORDER, POLICIES } from "../data/policies";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LOGO_URL =
  "https://i.ibb.co/WvGVVmBf/too-much-highlights-and-white-202606030034-modified-1.png";

const POLICY_LINKS = POLICY_ORDER.map((slug) => ({
  to: `/policy/${slug}`,
  label: POLICIES[slug].title,
  id: `policy-${slug}`,
}));

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [mobilePolicyOpen, setMobilePolicyOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setPolicyOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isActive = (to) =>
    to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 backdrop-blur-2xl bg-black/70 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <img
            src={cdnImg(LOGO_URL, 80)}
            alt="NeelVarma"
            width="40"
            height="40"
            decoding="async"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-md object-cover shadow-[0_0_20px_rgba(255,77,57,0.35)]"
          />
          <div className="leading-tight">
            <div className="font-semibold tracking-tight text-base text-white">NeelVarma</div>
            <div className="text-[9px] sm:text-[10px] text-zinc-500 font-mono tracking-[0.18em] uppercase">
              Gareebo Ka Filmmaker
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7 text-xs font-mono tracking-[0.18em] uppercase text-zinc-400">
          <Link
            to="/premium"
            data-testid="nav-premium"
            className={`hover:text-white transition-colors flex items-center gap-1.5 ${
              isActive("/premium") ? "text-white" : ""
            }`}
          >
            <Sparkle size={11} weight="fill" className="text-red-400" />
            Premium Addons
          </Link>
          <Link
            to="/studio"
            data-testid="nav-studio"
            className={`hover:text-white transition-colors ${isActive("/studio") ? "text-white" : ""}`}
          >
            Studio
          </Link>
          <Link
            to="/creator-os"
            data-testid="nav-creator-os"
            className={`hover:text-white transition-colors flex items-center gap-1.5 ${
              isActive("/creator-os") ? "text-white" : ""
            }`}
          >
            <Sparkle size={11} weight="fill" className="text-emerald-400" />
            YouTube Growth
          </Link>
          <Link
            to="/blog"
            data-testid="nav-blog"
            className={`hover:text-white transition-colors ${isActive("/blog") ? "text-white" : ""}`}
          >
            Blog
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              {user.is_admin && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link"
                  className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-100"
                >
                  <Star size={10} weight="fill" /> Admin
                </Link>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu-trigger"
                    className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/15 text-zinc-200 hover:border-red-500/50 hover:text-white transition focus:outline-none focus:ring-1 focus:ring-red-500/40"
                  >
                    <User size={11} weight="fill" />
                    <span className="max-w-[120px] truncate normal-case tracking-normal text-zinc-300">
                      {user.name || user.email?.split("@")[0]}
                    </span>
                    <CaretDown size={10} weight="bold" className="text-zinc-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  data-testid="nav-user-menu"
                  align="end"
                  className="bg-zinc-950 border border-white/10 text-zinc-200 min-w-[200px] mt-1"
                >
                  <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">
                    {user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    data-testid="nav-menu-dashboard"
                    onSelect={() => navigate("/dashboard")}
                    className="cursor-pointer focus:bg-white/[0.06] focus:text-white"
                  >
                    <SquaresFour size={13} weight="fill" className="mr-2 text-zinc-400" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="nav-menu-invoices"
                    onSelect={() => navigate("/invoices")}
                    className="cursor-pointer focus:bg-white/[0.06] focus:text-white"
                  >
                    <Receipt size={13} weight="fill" className="mr-2 text-zinc-400" />
                    My Invoices
                  </DropdownMenuItem>
                  {user.is_partner && !user.is_admin && (
                    <DropdownMenuItem
                      data-testid="nav-menu-partner"
                      onSelect={() => navigate("/partner")}
                      className="cursor-pointer focus:bg-violet-500/10 focus:text-violet-200"
                    >
                      <Handshake size={13} weight="fill" className="mr-2 text-violet-400" />
                      Partner Console
                    </DropdownMenuItem>
                  )}
                  {user.is_admin && (
                    <DropdownMenuItem
                      data-testid="nav-menu-admin"
                      onSelect={() => navigate("/admin")}
                      className="cursor-pointer focus:bg-amber-500/10 focus:text-amber-200"
                    >
                      <Star size={13} weight="fill" className="mr-2 text-amber-400" />
                      Admin panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    data-testid="nav-menu-logout"
                    onSelect={logout}
                    className="cursor-pointer focus:bg-red-500/10 focus:text-red-300 text-zinc-300"
                  >
                    <SignOut size={13} className="mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                data-testid="nav-logout-btn"
                variant="ghost"
                size="sm"
                onClick={logout}
                className="sm:hidden text-zinc-400 hover:text-white px-2"
                aria-label="Log out"
              >
                <SignOut size={16} />
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                className="hidden sm:flex text-zinc-300 hover:text-white"
              >
                Login
              </Button>
              <Button
                data-testid="nav-signup-btn"
                size="sm"
                onClick={() => navigate("/signup")}
                className="bg-red-600 hover:bg-red-500 text-white rounded-lg shadow-[0_0_20px_rgba(255,77,57,0.35)] hover:shadow-[0_0_30px_rgba(255,77,57,0.55)]"
              >
                Get Started
              </Button>
            </>
          )}

          <button
            type="button"
            data-testid="nav-hamburger"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="md:hidden ml-1 w-10 h-10 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center text-zinc-300 hover:text-white hover:border-red-500/40"
          >
            {open ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-white/5 bg-black/95 backdrop-blur-2xl"
        >
          <nav className="px-4 py-4 flex flex-col gap-1 text-sm">
            <Link
              to="/premium"
              data-testid="nav-mobile-premium"
              onClick={() => setOpen(false)}
              className="px-3 py-3 rounded-lg text-zinc-300 hover:text-white hover:bg-white/[0.04] flex items-center gap-2"
            >
              <Sparkle size={12} weight="fill" className="text-red-400" /> Premium Addons
            </Link>
            <Link
              to="/studio"
              data-testid="nav-mobile-studio"
              onClick={() => setOpen(false)}
              className="px-3 py-3 rounded-lg text-zinc-300 hover:text-white hover:bg-white/[0.04]"
            >
              Studio
            </Link>
            <Link
              to="/creator-os"
              data-testid="nav-mobile-creator-os"
              onClick={() => setOpen(false)}
              className="px-3 py-3 rounded-lg text-zinc-300 hover:text-white hover:bg-white/[0.04] flex items-center gap-2"
            >
              <Sparkle size={12} weight="fill" className="text-emerald-400" /> YouTube Growth
            </Link>
            <Link
              to="/blog"
              data-testid="nav-mobile-blog"
              onClick={() => setOpen(false)}
              className="px-3 py-3 rounded-lg text-zinc-300 hover:text-white hover:bg-white/[0.04]"
            >
              Blog
            </Link>
            {!user && (
              <Link
                to="/login"
                data-testid="nav-mobile-login"
                onClick={() => setOpen(false)}
                className="px-3 py-3 rounded-lg text-red-400 hover:bg-white/[0.04]"
              >
                Login
              </Link>
            )}
            {user && (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-mobile-dashboard"
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 rounded-lg text-zinc-300 hover:text-white hover:bg-white/[0.04] flex items-center gap-2"
                >
                  <User size={12} weight="fill" /> Dashboard
                </Link>
                <Link
                  to="/invoices"
                  data-testid="nav-mobile-invoices"
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 rounded-lg text-zinc-300 hover:text-white hover:bg-white/[0.04] flex items-center gap-2"
                >
                  <Receipt size={12} weight="fill" /> My Invoices
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
